import React, { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@foodamigos/ui';
import { INTERCOM_COMPANION_SELECTOR } from '../../widgets/IntercomWidget';
import { getIntercomUnreadCount, subscribeIntercomUnreadCount } from '../../services/intercom/intercomUnread';

const ID = INTERCOM_COMPANION_SELECTOR.slice(1);

// Intercom's messenger icon (its launcher's speech bubble, as the design's IntercomMark draws it), 18 px tall like
// Ask Amigo's icon; the width follows from its 28 × 32 box.
const ICON_HEIGHT = 18;
const ICON_PATH =
  'M28 32s-4.714-1.855-8.527-3.34H3.437C1.54 28.66 0 27.026 0 25.013V3.644C0 1.633 1.54 0 3.437 0h21.125c1.898 0 ' +
  '3.437 1.632 3.437 3.645v18.404H28V32zm-4.139-11.982a.88.88 0 00-1.292-.105c-.03.026-3.015 2.681-8.57 2.681-5.486 ' +
  '0-8.517-2.636-8.571-2.684a.88.88 0 00-1.29.107 1.01 1.01 0 00-.219.708.992.992 0 00.318.664c.142.128 3.537 3.15 ' +
  '9.762 3.15 6.226 0 9.621-3.022 9.763-3.15a.992.992 0 00.317-.664 1.01 1.01 0 00-.218-.707z';

function IntercomIcon() {
  return (
    <svg
      viewBox="0 0 28 32"
      height={ICON_HEIGHT}
      width={(ICON_HEIGHT * 28) / 32}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      data-icon="intercom"
      style={{ color: 'var(--fa-ai-to)' }}
    >
      <path d={ICON_PATH} />
    </svg>
  );
}

/**
 * Intercom's launcher as Ask Amigo's companion: a white 48 px circle with the button's hairline and shadow, Intercom's
 * own chat icon in Amigo's green (a partner's colour on theirs), Intercom's unread count. IntercomWidget boots
 * Intercom with `custom_launcher_selector` on it (and `hide_default_launcher`), so Intercom's own click handler opens
 * the Messenger, as its launcher would.
 */
export default function PlatformAgentIntercomLauncher() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:PlatformAgent:PlatformAgentIntercomLauncher' });
  const unread = useSyncExternalStore(subscribeIntercomUnreadCount, getIntercomUnreadCount);

  return (
    <button
      id={ID}
      type="button"
      aria-label={unread > 0 ? t('label_unread', { count: unread }) : t('label')}
      className="relative flex h-12 w-12 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid bg-white p-0 hover:bg-[var(--fa-color-surface-hover)]"
      style={{ borderColor: 'var(--fa-color-border-strong)', boxShadow: 'var(--fa-shadow-float)' }}
    >
      <IntercomIcon />
      {unread > 0 && (
        <Badge variant="danger" aria-hidden="true" className="absolute -right-1 -top-1">
          {String(unread)}
        </Badge>
      )}
    </button>
  );
}
